import { apiClient } from '@/lib/api-client';
import type { RecapResponse } from '../types/recap';

interface Child {
  id: string;        // Student profile ID — use for /students/:id endpoints
  user_id: string;   // User ID — use for assignments (assignee_id FK) and activity logs
  name: string;
  avatar?: string;
  grade?: number;
  readingStreak?: number;
  lastActive?: string;
  fluency_enabled?: boolean;
  music_enabled?: boolean;
}

interface ChildStats {
  readingStreak: number;
  streakChange?: number;
  storiesRead: number;
  storiesChange?: number;
  cardsMastered: number;
  cardsChange?: number;
  avgQuizScore?: number;
  needsAttention?: boolean;
  attentionReason?: string;
}

interface Assignment {
  id: string;
  title: string;
  dueDate: string;
  status: 'pending' | 'in_progress' | 'completed' | 'overdue';
  type: 'story' | 'flashcards' | 'quiz';
}

interface Resource {
  id: string;
  title: string;
  description?: string;
  difficulty?: string;
  cardCount?: number;
  readTime?: number;
}

interface Activity {
  id: string;
  type: string;
  childId: string;
  childName?: string;
  timestamp: string;
  data: Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any -- activity data is arbitrary JSON from backend
}

interface BackendStudent {
  id: string;
  user_id: string;
  display_name?: string | null;
  first_name?: string | null;
  avatar?: string | null;
  grade_level?: number | null;
  reading_streak?: number;
  last_activity?: string | null;
  updated_at?: string | null;
  fluency_enabled?: boolean;
  music_enabled?: boolean;
}

interface CreateAssignmentData {
  assignee_id: string;
  assignable_type: string;
  assignable_id: string;
  title: string;
  instructions?: string;
  due_date?: string;
  assignment_metadata?: Record<string, unknown>;
}

export const parentApi = {
  // Children (using /me/students endpoint - role-aware)
  getChildren: async (): Promise<Child[]> => {
    const data = await apiClient.get<BackendStudent[] | { students: BackendStudent[] }>('/me/students');
    // The API returns either an array directly or wrapped in { students: [...] }
    const students = Array.isArray(data) ? data : (data.students || []);

    // Transform backend data structure to match our Child interface
    return students.map((student: BackendStudent) => ({
      id: student.id,  // Student profile ID
      user_id: student.user_id,  // User ID (for assignments)
      name: student.display_name || student.first_name || 'Student',
      avatar: student.avatar ?? undefined,
      grade: student.grade_level ?? undefined,
      readingStreak: student.reading_streak || 0,
      lastActive: student.last_activity ?? student.updated_at ?? undefined,
      fluency_enabled: student.fluency_enabled ?? false,
      music_enabled: student.music_enabled ?? false,
    }));
  },

  getChild: async (childId: string): Promise<Child> => {
    const student = await apiClient.get<BackendStudent>(`/me/students/${childId}`);

    // Transform backend data structure to match our Child interface
    return {
      id: student.id,  // Student profile ID
      user_id: student.user_id,  // User ID (for assignments)
      name: student.display_name || student.first_name || 'Student',
      avatar: student.avatar ?? undefined,
      grade: student.grade_level ?? undefined,
      readingStreak: student.reading_streak || 0,
      lastActive: student.last_activity ?? student.updated_at ?? undefined,
      fluency_enabled: student.fluency_enabled ?? false,
      music_enabled: student.music_enabled ?? false,
    };
  },

  // Stats (using /students endpoint for child resources)
  getChildStats: async (childId: string): Promise<ChildStats> => {
    return apiClient.get<ChildStats>(`/students/${childId}/progress`);
  },

  // Assignments (using query parameter for child filtering)
  getChildAssignments: async (childId: string): Promise<Assignment[]> => {
    return apiClient.get<Assignment[]>('/assignments', {
      params: { child_id: childId, include_completed: false }
    });
  },

  createAssignment: async (data: CreateAssignmentData | Record<string, any>): Promise<Assignment> => { // eslint-disable-line @typescript-eslint/no-explicit-any -- mobile uses different shape
    return apiClient.post('/assignments', data);
  },

  // Resources
  getAvailableStories: async (params?: { search?: string }): Promise<Resource[]> => {
    return apiClient.get<Resource[]>('/stories', { params });
  },

  getAvailableDecks: async (params?: { search?: string }): Promise<Resource[]> => {
    return apiClient.get<Resource[]>('/flashcards/decks', { params });
  },

  getAvailableThemes: async (params?: { search?: string }): Promise<Resource[]> => {
    return apiClient.get<Resource[]>('/stories/themes', { params });
  },

  // Recommendations
  getChildRecommendations: async (childId: string, limit = 10) => {
    return apiClient.get<{
      student_id: string;
      grade_level: number | null;
      is_cold_start: boolean;
      recommended_stories: Array<{
        story_id: string;
        title: string;
        theme_id: string;
        theme_slug: string;
        theme_label: string;
        word_count: number | null;
        recommendation_score: number;
        reason: string;
      }>;
    }>(`/recommendations/child/${childId}`, { params: { limit } });
  },

  // Activities
  getChildActivities: async (childId?: string | null, limit = 10): Promise<Activity[]> => {
    const params = childId ? { user_id: childId, limit } : { limit };
    return apiClient.get<Activity[]>('/activities', { params });
  },

  // Weekly recap
  async getWeeklyRecap(studentId: string): Promise<RecapResponse> {
    return apiClient.get<RecapResponse>(`/analytics/student/${studentId}/recap`);
  },
};